/**
 * Realtime feed: WebSocket `/api/v1/ws` with reconnect + backoff, or — in mock mode — a simulator
 * that delivers a new customer message every ~20 s through the mock backend.
 */
import { isAiModeEvent, type AiModeEvent } from './aimode/types'
import { onMockAiMode } from './aimode/mock'
import { isCollabEvent, type CollabEvent } from './collab/types'
import { mockPresenceEcho, onMockCollab, startMockPresence } from './collab/mock'
import { isConversationRemovedEvent, onMockRemoved, type ConversationRemovedEvent } from './devreset'
import type { MockChatApi } from './mock'
import { onMockSocial, toSocialChangedEvent, type SocialChangedEvent } from './posts/live'
import { onMockScript } from './script/mock'
import { isScriptUpdatedEvent, toScriptUpdatedEvent, type ScriptUpdatedEvent } from './script/types'
import type { AiDraftEvent, RealtimeEvent, StaffAvailabilityEvent, TeamChangedEvent } from './types'

export type RealtimeStatus = 'connecting' | 'open' | 'reconnecting' | 'closed'

export interface RealtimeHandlers {
  onEvent(e: RealtimeEvent): void
  /**
   * Events may have been missed — refetch the list, the open chat, …: after a reconnect, and when the page comes back
   * after being hidden for a while (laptop lid, another tab) while the socket still looks alive.
   */
  onResync(): void
  onStatus?(s: RealtimeStatus): void
  /** Teamwork events on the same socket: presence, notes, tags (api/app/collab). */
  onCollab?(e: CollabEvent): void
  /** AI mode (docs/ai-mode.md): ai.handoff alert · ai.turn (refresh the side card). */
  onAiMode?(e: AiModeEvent): void
  /** "ล้างแชทนี้ (ทดสอบ)" deleted a chat (api/app/devreset.py): drop it from every list and thread. */
  onRemoved?(e: ConversationRemovedEvent): void
  /** Someone's พร้อมรับ changed (their tap, or switched off after no activity — docs/routing.md). */
  onAvailability?(e: StaffAvailabilityEvent): void
  /** A web chat visitor's website context changed (cart, step, messages — docs/webchat.md): reload its card. */
  onWebContext?(e: WebContextEvent): void
  /** "สคริปต์แนะนำรายคน": the API recomputed a chat's script (api/script) — reload it if that chat is open. */
  onScript?(e: ScriptUpdatedEvent): void
  /** The team changed (members, caps, shifts, routing settings): re-read the team and the team-load strip. */
  onTeam?(e: TeamChangedEvent): void
  /** An AI draft of a chat is ready (auto draft / another screen's run): read it if that chat is open. */
  onAiDraft?(e: AiDraftEvent): void
  /** หน้าโพสต์ changed (AI done, approved, published, settings — api/posts/live.ts): /posts reads it again. */
  onSocial?(e: SocialChangedEvent): void
  /** Extra fields for every heartbeat ping: `idle_s` = seconds since real input in any tab (lib/activity.ts). */
  pingExtra?(): Record<string, unknown>
}

export function isTeamChangedEvent(v: unknown): v is TeamChangedEvent {
  return typeof v === 'object' && v !== null && (v as { type?: unknown }).type === 'team.changed'
}

export function isAiDraftEvent(v: unknown): v is AiDraftEvent {
  if (typeof v !== 'object' || v === null) return false
  const e = v as { type?: unknown; conversation_id?: unknown }
  return e.type === 'ai.draft' && typeof e.conversation_id === 'string'
}

export function isAvailabilityEvent(v: unknown): v is StaffAvailabilityEvent {
  if (typeof v !== 'object' || v === null) return false
  const e = v as { type?: unknown; staff?: unknown; available?: unknown }
  return e.type === 'staff.availability' && typeof e.staff === 'object' && e.staff !== null && typeof e.available === 'boolean'
}

/** The heartbeat message: `{"type":"ping", "idle_s": n}` (the server answers pong and records activity). */
export function pingMessage(h: Pick<RealtimeHandlers, 'pingExtra'>): string {
  let extra: Record<string, unknown> = {}
  try {
    extra = h.pingExtra?.() ?? {}
  } catch {
    extra = {}
  }
  return JSON.stringify({ type: 'ping', ...extra })
}

export interface RealtimeConnection {
  close(): void
  /** Send a small JSON message on the socket (presence). Dropped when not connected. */
  send?(msg: unknown): void
  /** Heartbeat now (activity after a pause: the server should know before its next routing pass). */
  ping?(): void
}

/** Exponential backoff with jitter: 1 s, 2 s, 4 s … capped at 30 s. */
export function backoffDelay(attempt: number, random: () => number = Math.random, minMs = 1000, maxMs = 30000): number {
  const base = Math.min(maxMs, minMs * 2 ** Math.max(0, attempt))
  return Math.round(base / 2 + (random() * base) / 2)
}

export interface WebContextEvent {
  type: 'webchat.context'
  conversation_id: string
}

export function isWebContextEvent(v: unknown): v is WebContextEvent {
  if (typeof v !== 'object' || v === null) return false
  const e = v as { type?: unknown; conversation_id?: unknown }
  return e.type === 'webchat.context' && typeof e.conversation_id === 'string'
}

export function isRealtimeEvent(v: unknown): v is RealtimeEvent {
  if (typeof v !== 'object' || v === null) return false
  const e = v as { type?: unknown; conversation?: unknown }
  return (
    (e.type === 'message.created' ||
      e.type === 'message.updated' ||
      e.type === 'message.removed' ||
      e.type === 'conversation.updated' ||
      e.type === 'ai.steps') &&
    typeof e.conversation === 'object'
  )
}

interface SocketLike {
  onopen: ((ev: Event) => void) | null
  onclose: ((ev: CloseEvent) => void) | null
  onerror: ((ev: Event) => void) | null
  onmessage: ((ev: MessageEvent) => void) | null
  send(data: string): void
  close(code?: number): void
}

export interface SocketOptions {
  createSocket?: (url: string) => SocketLike
  random?: () => number
  /** Send a ping this often; if nothing arrives for 2 intervals, reconnect. */
  heartbeatMs?: number
}

/** Back after the page was hidden this long: check the line and resync (events may have been missed meanwhile). */
export const WAKE_AFTER_MS = 30000
/** After a wake-up ping: nothing heard back within this → the socket is dead, open a new one. */
export const PROBE_MS = 5000

export class RealtimeSocket implements RealtimeConnection {
  private ws: SocketLike | null = null
  /** `ws` is open (not still connecting). */
  private live = false
  private attempt = 0
  private everOpened = false
  private stopped = false
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private beatTimer: ReturnType<typeof setInterval> | null = null
  private probeTimer: ReturnType<typeof setTimeout> | null = null
  private hiddenAt: number | null = null
  private lastSeen = 0
  private readonly create: (url: string) => SocketLike
  private readonly random: () => number
  private readonly heartbeatMs: number

  constructor(
    private readonly url: string,
    private readonly h: RealtimeHandlers,
    opts: SocketOptions = {},
  ) {
    this.create = opts.createSocket ?? ((u) => new WebSocket(u) as unknown as SocketLike)
    this.random = opts.random ?? Math.random
    this.heartbeatMs = opts.heartbeatMs ?? 25000
    this.connect()
  }

  private connect(): void {
    if (this.stopped) return
    this.h.onStatus?.(this.everOpened ? 'reconnecting' : 'connecting')
    const ws = this.create(this.url)
    this.ws = ws
    ws.onopen = () => {
      this.attempt = 0
      this.live = true
      this.lastSeen = Date.now()
      this.h.onStatus?.('open')
      if (this.everOpened) this.h.onResync()
      this.everOpened = true
      this.startHeartbeat()
      // tell the server right away how long nobody touched the screen (after a reconnect / laptop wake-up)
      if (this.h.pingExtra) this.ping()
    }
    ws.onmessage = (ev) => {
      this.lastSeen = Date.now()
      let data: unknown
      try {
        data = JSON.parse(String(ev.data))
      } catch {
        return
      }
      const social = toSocialChangedEvent(data)
      if (isRealtimeEvent(data)) this.h.onEvent(data)
      else if (social) this.h.onSocial?.(social)
      else if (isAvailabilityEvent(data)) this.h.onAvailability?.(data)
      else if (isTeamChangedEvent(data)) this.h.onTeam?.(data)
      else if (isAiDraftEvent(data)) this.h.onAiDraft?.(data)
      else if (isCollabEvent(data)) this.h.onCollab?.(data)
      else if (isAiModeEvent(data)) this.h.onAiMode?.(data)
      else if (isConversationRemovedEvent(data)) this.h.onRemoved?.(data)
      else if (isWebContextEvent(data)) this.h.onWebContext?.(data)
      else if (isScriptUpdatedEvent(data)) this.h.onScript?.(toScriptUpdatedEvent(data)!) // numeric id → string
    }
    ws.onerror = () => {
      /* onclose follows */
    }
    ws.onclose = (ev) => {
      this.stopHeartbeat()
      this.clearProbe()
      if (this.ws === ws) {
        this.ws = null
        this.live = false
      }
      if (ev?.code === 4001) {
        // not signed in (session expired / logged out): retrying cannot help
        this.stopped = true
        this.h.onStatus?.('closed')
        return
      }
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.retryTimer) return
    const wait = backoffDelay(this.attempt, this.random)
    this.attempt += 1
    this.h.onStatus?.('reconnecting')
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.connect()
    }, wait)
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    this.beatTimer = setInterval(() => {
      if (Date.now() - this.lastSeen > this.heartbeatMs * 2) {
        // nothing for 2 heartbeats: the line is dead. close() on a dead connection can take minutes to report back,
        // so drop it and open a new one now (its open resyncs)
        this.reconnectNow()
        return
      }
      this.ping()
    }, this.heartbeatMs)
  }

  /** Let go of the socket without waiting for its close event (a dead connection may report it only minutes later). */
  private drop(): void {
    const ws = this.ws
    this.ws = null
    this.live = false
    this.stopHeartbeat()
    this.clearProbe()
    if (!ws) return
    ws.onopen = null
    ws.onmessage = null
    ws.onerror = null
    ws.onclose = null
    try {
      ws.close(4000)
    } catch {
      /* already closed */
    }
  }

  /** A new socket now (the held one dropped), without the backoff wait. */
  private reconnectNow(): void {
    if (this.stopped) return
    this.drop()
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = null
    this.attempt = 0
    this.connect()
  }

  private clearProbe(): void {
    if (this.probeTimer) clearTimeout(this.probeTimer)
    this.probeTimer = null
  }

  /**
   * The page was hidden / shown again (document visibilitychange). Back after more than 30 s (timers sleep in a hidden
   * tab, a laptop lid was closed): a socket that heard nothing for 2 heartbeats is replaced at once; one that looks
   * alive is asked (ping — no answer within 5 s → replaced) and the screen resyncs meanwhile.
   */
  visibility(visible: boolean, now: number = Date.now()): void {
    if (!visible) {
      if (this.hiddenAt === null) this.hiddenAt = now
      return
    }
    const since = this.hiddenAt
    this.hiddenAt = null
    if (since === null || now - since <= WAKE_AFTER_MS || this.stopped) return
    if (this.ws && !this.live) return // still connecting: its open resyncs
    if (!this.ws || Date.now() - this.lastSeen > this.heartbeatMs * 2) {
      this.reconnectNow()
      return
    }
    this.h.onResync()
    const asked = Date.now()
    this.ping()
    this.clearProbe()
    this.probeTimer = setTimeout(() => {
      this.probeTimer = null
      if (this.lastSeen < asked) this.reconnectNow()
    }, PROBE_MS)
  }

  /** One heartbeat now (also used when someone comes back after a pause — lib/activity.ts onWake). */
  ping(): void {
    try {
      this.ws?.send(pingMessage(this.h))
    } catch {
      /* closing */
    }
  }

  private stopHeartbeat(): void {
    if (this.beatTimer) clearInterval(this.beatTimer)
    this.beatTimer = null
  }

  /** Presence etc.: only while the socket is open (a missed heartbeat simply expires on the other screens). */
  send(msg: unknown): void {
    try {
      this.ws?.send(JSON.stringify(msg))
    } catch {
      /* connecting / closing */
    }
  }

  /**
   * The browser came back online: reconnect now instead of waiting out the backoff — also when a socket is held (it
   * belonged to the old network and is most likely dead; the new one's open resyncs).
   */
  nudge(): void {
    this.reconnectNow()
  }

  close(): void {
    this.stopped = true
    this.stopHeartbeat()
    this.clearProbe()
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = null
    this.ws?.close(1000)
    this.ws = null
    this.live = false
    this.h.onStatus?.('closed')
  }
}

/** `ws(s)://…/api/v1/ws` from the HTTP base. Same origin, so the browser sends the session cookie. */
export function wsUrl(apiBase: string): string {
  const u = new URL(`${apiBase.replace(/\/+$/, '')}/api/v1/ws`, window.location.origin)
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:'
  return u.toString()
}

export function connectSocket(apiBase: string, h: RealtimeHandlers): RealtimeConnection {
  const sock = new RealtimeSocket(wsUrl(apiBase), h)
  const online = () => sock.nudge()
  const vis = () => sock.visibility(document.visibilityState !== 'hidden')
  window.addEventListener('online', online)
  document.addEventListener('visibilitychange', vis)
  if (document.visibilityState === 'hidden') vis() // opened in a background tab
  return {
    close() {
      window.removeEventListener('online', online)
      document.removeEventListener('visibilitychange', vis)
      sock.close()
    },
    send: (msg: unknown) => sock.send(msg),
    ping: () => sock.ping(),
  }
}

/** Mock mode: forward the fake backend's events and make a customer write every ~20 s. */
export function connectMock(api: MockChatApi, h: RealtimeHandlers, everyMs = 20000): RealtimeConnection {
  const off = api.subscribe((e) => h.onEvent(e))
  const offCollab = onMockCollab((e) => h.onCollab?.(e))
  const offAiMode = onMockAiMode((e) => h.onAiMode?.(e))
  const offRemoved = onMockRemoved((e) => h.onRemoved?.(e))
  const offScript = onMockScript((e) => h.onScript?.(e))
  const offSocial = onMockSocial((e) => h.onSocial?.(e))
  const offAvail = api.onAvailability((e) => h.onAvailability?.(e))
  const offExtra = api.onExtra((e) => (e.type === 'team.changed' ? h.onTeam?.(e) : h.onAiDraft?.(e)))
  const stopPresence = startMockPresence()
  // the mock "server" hears the heartbeat too, so พร้อมรับ auto-off can be seen without a backend
  const beat = () => api.heartbeat(JSON.parse(pingMessage(h)) as { idle_s?: unknown })
  const beatTimer = setInterval(beat, 25000)
  h.onStatus?.('open')
  let timer: ReturnType<typeof setTimeout> | null = null
  const loop = () => {
    // 16–24 s so it does not feel mechanical
    timer = setTimeout(
      () => {
        api.simulateInbound()
        loop()
      },
      everyMs * (0.8 + Math.random() * 0.4),
    )
  }
  loop()
  const me = api.db.team.find((m) => m.id === api.db.meId)
  return {
    close() {
      if (timer) clearTimeout(timer)
      clearInterval(beatTimer)
      off()
      offCollab()
      offAiMode()
      offRemoved()
      offScript()
      offSocial()
      offAvail()
      offExtra()
      stopPresence()
      h.onStatus?.('closed')
    },
    send: (msg: unknown) => mockPresenceEcho(msg, me ? { id: me.id, name: me.name } : null),
    ping: beat,
  }
}
